import styled, { css, DefaultTheme } from "styled-components";
import { theme } from "../../../constants/StyledComponents";
import { InputBorderEnum, InputSizes } from "./Input";

const sizeVariantStyles = (theme: DefaultTheme, size: InputSizes) =>
  ({
    small: css`
      height: ${theme.variables.height_1};
    `,
    medium: css`
      height: ${theme.variables.height_2};
    `,
    regular: css`
      height: ${theme.variables.height_3};
    `,
  })[size];

const borderVariantStyles = (theme: DefaultTheme, border: InputBorderEnum) =>
  ({
    default: css`
      border: 1px solid;
      border-radius: ${theme.variables.border_radius_1};
    `,
    bottom: css`
      border-bottom: 1px solid;
    `,
  })[border];

const StyledInputContainer = styled.div<{
  $readOnly?: boolean;
  disabled?: boolean;
  $hasError?: boolean;
  $inputSize: InputSizes;
  $borderVariant: InputBorderEnum;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ theme, $inputSize }) => sizeVariantStyles(theme, $inputSize)}
  ${({ theme, $borderVariant }) => borderVariantStyles(theme, $borderVariant)}
  border-color: ${(props) =>
    props.disabled
      ? theme.colors.grey_4
      : props.$hasError
        ? theme.colors.red_1
        : theme.colors.black};

  background-color: ${theme.colors.white};
  position: relative;
  box-sizing: border-box;

  ${({ disabled }) =>
    disabled &&
    css`
      &,
      & * {
        cursor: not-allowed !important;
      }
    `}

  &:focus-within {
    border-color: ${theme.colors.purple};
  }
`;

const calcPadding = (
  hasEndIcon?: boolean,
  allowClear?: boolean,
  hasCount?: boolean,
) => {
  let res = 0;

  if (allowClear) {
    res += 36;
  }

  if (hasEndIcon) {
    res += 36;
  }

  if (hasCount) {
    res += 16;
  }

  return res;
};

const calcLeftPadding = (hasStartIcon?: boolean) => {
  return hasStartIcon ? 12 + 28 : 12;
};

const StyledInput = styled.input<{
  $hasEndIcon?: boolean;
  $hasStartIcon?: boolean;
  readOnly?: boolean;
  $allowClear?: boolean;
  $hasLabel?: boolean;
  $hasCount?: boolean;
}>`
  background-color: transparent;
  outline: 0;
  border: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding: ${(props) => (props.$hasLabel ? "8" : "0")}px
    ${(props) =>
      calcPadding(props.$hasEndIcon, props.$allowClear, props.$hasCount)}px
    0px ${(props) => calcLeftPadding(props.$hasStartIcon)}px;

  ${({ $hasEndIcon, $allowClear, $hasCount, $hasStartIcon }) => css`
    width: calc(
      100% -
        ${calcPadding($hasEndIcon, $allowClear, $hasCount) +
        calcLeftPadding($hasStartIcon)}px
    );
  `};
  color: ${theme.colors.black};
  font-family: ${theme.variables.font_family};
  font-size: ${theme.variables.fs_14};
  font-weight: ${theme.variables.fw_Regular};
  cursor: ${(props) =>
    props.disabled ? "not-allowed" : props.readOnly ? "inherit" : "text"};

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    border-radius: 8px;
  }

  &:focus {
    outline: 0;
    outline-offset: 0;
    border-color: transparent;
    --tw-ring-offset-width: 0;
  }

  &:-webkit-autofill + label {
    transform: translate(2px, 4px) scale(0.8) !important;
  }
`;

const StyledLabel = styled.label<{
  $hasError?: boolean;
  $hasEndIcon?: boolean;
  $hasStartIcon?: boolean;
  $allowClear?: boolean;
  $hasCount?: boolean;
  $inputSize: InputSizes;
  $isActive: boolean;
  $hasPlaceholder?: boolean;
}>`
  pointer-events: none;
  position: absolute;
  padding-left: 12px;
  transform-origin: top left;
  transition: all 0.2s ease-out;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ $hasEndIcon, $allowClear, $hasCount }) => css`
    max-width: calc(
      100% - ${calcPadding($hasEndIcon, $allowClear, $hasCount) + 10}px
    );
  `};
  top: ${(props) => (props.$inputSize === InputSizes.SMALL ? "-8px" : "0")};
  transform: ${(props) => {
    const leftOffset = props.$hasStartIcon ? 28 : 0;
    if (props.$isActive || props.$hasPlaceholder) {
      if (props.$inputSize === InputSizes.SMALL) {
        return `translate(${leftOffset + 4}px, 7px) scale(0.7)`;
      }
      return `translate(${leftOffset + 2}px, 4px) scale(0.8)`;
    }
    const inactiveTop =
      props.$inputSize === InputSizes.SMALL
        ? 12
        : props.$inputSize === InputSizes.MEDIUM
          ? 8
          : 13;
    return `translate(${leftOffset}px, ${inactiveTop}px) scale(1)`;
  }};

  ${StyledInputContainer}:focus-within & {
    transform: ${(props) =>
      props.$inputSize === InputSizes.SMALL
        ? `translate(${props.$hasStartIcon ? 30 : 2}px, 7px) scale(0.7)`
        : `translate(${props.$hasStartIcon ? 30 : 2}px, 4px) scale(0.8)`};
  }
`;

const StyledFieldContainer = styled.div<{
  $hasError?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$hasError ? "8px" : "0")};
  width: 100%;
  align-items: start;
`;

const StyledIconContainer = styled.div<{ $disabled?: boolean }>`
  position: absolute;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  gap: 8px;
  right: 12px;
`;

const StyledStartIconContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  height: 100%;
  left: 12px;
`;

const StyledCloseContainer = styled.div<{ $visible: boolean }>`
  cursor: ${(props) => props.$visible && "pointer"};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
`;

export {
  StyledInput,
  StyledLabel,
  StyledInputContainer,
  StyledFieldContainer,
  StyledIconContainer,
  StyledStartIconContainer,
  StyledCloseContainer,
};
